import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import { animated, useTransition } from 'react-spring'

import { ElemyLogo } from '@ui/blocks/ElemyLogo'
import TopMenu from '@ui/business-components/Header/TopMenu'
import ZipCodeForm from '@ui/business-components/ZipCodeForm/ZipCodeForm'
import HorizontalProgressBar from '@ui/business-components/HorizontalProgressBar'
import { HEADER_HEIGHT } from '@app/ui/theme/constants'
import { ROUTES } from '@app/constants'

import LoginSection from './LoginSection'

export interface HeaderWrapperProps {
  isMenuSticky: boolean
  transparent?: boolean
  isMobileOpen?: boolean
}

export interface HeaderProps {
  transparent?: boolean
  progressBar?: boolean
}

const StyledZipForm = styled(ZipCodeForm)`
  margin-left: 3.9rem;
  position: relative;
  input {
    height: 4.4rem;
    max-width: 18.3rem;
    font-size: 1.4rem;
  }
  button {
    line-height: 2.4rem;
  }
  p {
    display: none;
  }
  @media ${({ theme }) => theme.device.tablet} {
    display: none;
  }
`

const RightSectionWrapper = styled.div`
  display: flex;

  @media ${({ theme }) => theme.device.laptop} {
    display: none;
  }
`

const LeftSectionWrapper = styled.div`
  display: flex;

  @media ${({ theme }) => theme.device.laptop} {
    justify-content: space-between;
    width: 100%;
  }
`

const HeaderWrapper = styled.header<HeaderWrapperProps>`
  position: fixed;
  transition: all 0.4s;
  background-color: ${({ isMenuSticky, theme, transparent, isMobileOpen }) => {
    const nonStickyBg = transparent ? 'transparent' : theme.global.colors.beige

    if (isMobileOpen) {
      return theme.global.colors.floralWhite
    }

    return isMenuSticky ? theme.global.colors.floralWhite : nonStickyBg
  }};
  box-shadow: ${({ isMenuSticky }) => (isMenuSticky ? '0px 5px 20px rgba(0, 0, 0, 0.07)' : 'none')};
  width: 100%;
  z-index: 10;
  top: 0;
`

const StyledElemyLogo = styled(ElemyLogo)`
  display: block;
  margin-right: 3.8rem;

  @media ${({ theme }) => theme.device.laptopL} {
    margin-right: 2rem;
  }

  @media ${({ theme }) => theme.device.laptop} {
    margin-right: 3.8rem;
  }

  @media ${({ theme }) => theme.device.mobileL} {
    margin-right: 2.5rem;
  }
`

const HeaderInner = styled.div`
  display: flex;
  padding: 0 4.8rem;
  justify-content: space-between;
  align-items: center;
  height: ${HEADER_HEIGHT}rem;

  @media ${({ theme }) => theme.device.laptopL} {
    padding: 0 2rem;
  }
`

const LogoWithForm = styled.div`
  display: flex;
  align-items: center;
`

const AnimatedZipForm = animated(StyledZipForm)

export const Header = ({ transparent, progressBar }: HeaderProps) => {
  const [stickyMenu, setStickyMenu] = useState(false)
  const [showZipCodeForm, setShowZipCodeForm] = useState(false)
  const [loadingPercentage, setPercentage] = useState(0)
  const [isMobileOpen, setMobileOpen] = useState(false)
  const transitions = useTransition(showZipCodeForm, {
    from: { width: 0, opacity: 0 },
    enter: { width: 344, opacity: 1 },
    leave: { width: 0, opacity: 0 }
  })

  const getVerticalScrollPercentage = (elm: HTMLElement | null) => {
    if (!elm) return 0
    const p = document.body.parentNode as HTMLElement
    const scrollPercentage = ((elm.scrollTop || p.scrollTop) / (elm.scrollHeight - p.clientHeight)) * 100
    if (scrollPercentage > 100) {
      return 100
    } else if (scrollPercentage === 0) {
      // Required to force react re-render component
      return 0.001
    } else {
      return scrollPercentage
    }
  }

  useEffect(() => {
    const handleScroll = () => {
      const position = window.pageYOffset
      setStickyMenu(position > HEADER_HEIGHT)
      setShowZipCodeForm(position > 500)
      if (progressBar) {
        const boundary = document.getElementById('progress-bar-boundary')
        setPercentage(getVerticalScrollPercentage(boundary))
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [progressBar])

  const toggleMobileOpen = () => {
    setMobileOpen(!isMobileOpen)
  }

  return (
    <HeaderWrapper transparent={transparent} isMobileOpen={isMobileOpen} isMenuSticky={stickyMenu}>
      <HeaderInner>
        <LeftSectionWrapper>
          <LogoWithForm>
            <Link title="Elemy" to={ROUTES.homePage}>
              <StyledElemyLogo />
            </Link>
          </LogoWithForm>
          <TopMenu isMenuSticky={stickyMenu} onToggleMobileOpen={toggleMobileOpen} isMobileOpen={isMobileOpen} />
        </LeftSectionWrapper>
        <RightSectionWrapper>
          <LoginSection hideLoginSection={showZipCodeForm} />
          {transitions(
            (styles, item) =>
              item && (
                <animated.div style={styles}>
                  <AnimatedZipForm silentError />
                </animated.div>
              )
          )}
        </RightSectionWrapper>
      </HeaderInner>
      <HorizontalProgressBar progressPercentage={loadingPercentage} />
    </HeaderWrapper>
  )
}

export default Header
